import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface State {
  err: Error | null;
}

interface Props {
  children: ReactNode;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Utility OS error boundary:', err, info);
  }

  render(): ReactNode {
    if (this.state.err) {
      return (
        <div className="min-h-screen bg-bg text-fg flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="size-16 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 border-2 border-fg/85 dark:border-fg/40 shadow-sticker mx-auto mb-5 flex items-center justify-center rotate-[-3deg]">
              <AlertTriangle size={28} className="text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.3)]" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Something cracked</h1>
            <p className="text-sm text-fg-muted mt-2 leading-relaxed">
              This page hit a runtime error. You can refresh, head home, or copy the details below if you want to dig in.
            </p>
            <pre className="mt-5 px-3 py-2 rounded-lg bg-bg-inset border border-border text-xs font-mono text-left overflow-auto max-h-48 break-all whitespace-pre-wrap">
              {this.state.err.message}
              {this.state.err.stack ? '\n\n' + this.state.err.stack.split('\n').slice(0, 5).join('\n') : ''}
            </pre>
            <div className="mt-5 flex items-center justify-center gap-2">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-lg bg-accent text-accent-fg border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-sticker active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed transition-all"
              >
                <RefreshCw size={13} />
                Reload
              </button>
              <button
                onClick={() => {
                  window.location.href = '/';
                }}
                className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-lg bg-surface text-fg border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-sticker active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed transition-all"
              >
                <Home size={13} />
                Home
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
